import React, { useState, useEffect } from 'react';
import { checkGtagAvailability, logGtagDebugInfo, type GtagDebugInfo } from '../utils/gtag-debug';

interface GA4StatusIndicatorProps {
  showDetails?: boolean;
}

const GA4StatusIndicator: React.FC<GA4StatusIndicatorProps> = ({ showDetails = false }) => {
  const [debugInfo, setDebugInfo] = useState<GtagDebugInfo | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const checkStatus = () => {
      const info = checkGtagAvailability();
      setDebugInfo(info);
    };

    // Check immediately
    checkStatus();

    // Check again after a delay to catch async loading
    const timeout = setTimeout(checkStatus, 2000);

    // Show indicator
    setIsVisible(true);

    return () => clearTimeout(timeout);
  }, []);

  const handleToggleDetails = () => {
    logGtagDebugInfo();
  };

  if (!isVisible || !debugInfo || process.env.NODE_ENV !== 'development') {
    return null;
  }

  const isHealthy = debugInfo.isGtagAvailable && debugInfo.isDataLayerAvailable && debugInfo.scriptLoaded;
  const statusColor = isHealthy ? '#10b981' : '#ef4444';
  const statusText = isHealthy ? 'GA4 Ready' : 'GA4 Issues';

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        cursor: 'pointer',
        border: `2px solid ${statusColor}`,
      }}
      onClick={handleToggleDetails}
      title="Click to log detailed GA4 debug info to console"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: statusColor,
          }}
        />
        <span>{statusText}</span>
      </div>
      
      {showDetails && (
        <div style={{ marginTop: '4px', fontSize: '10px', opacity: 0.8 }}>
          <div>gtag: {debugInfo.isGtagAvailable ? '✓' : '✗'}</div>
          <div>dataLayer: {debugInfo.isDataLayerAvailable ? '✓' : '✗'}</div>
          <div>script: {debugInfo.scriptLoaded ? '✓' : '✗'}</div>
          {debugInfo.errors.length > 0 && (
            <div style={{ color: '#ef4444' }}>
              Errors: {debugInfo.errors.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GA4StatusIndicator;
